/* ============================================================
   pageDesign.js — Design system partagé (éditeur + page publique)
   + 13 TEMPLATES (inspirés des modèles bio-link) et réglages de tailles
   ============================================================ */
import {
  Type, Link as LinkIcon, Image as ImageIcon, Video, Music,
  Globe, MapPin, Send, Minus, ShoppingBag, Mail, Calendar,
  HelpCircle, Code, FileText, Timer, Star, Layout,
  Heart, MessageCircle, Users, Tag, Sparkles, Scissors, Gift, Phone,
} from 'lucide-react'
import {
  FaLinkedin, FaGithub, FaTwitter, FaInstagram, FaFacebook,
  FaYoutube, FaTiktok, FaWhatsapp, FaGoogle, FaSpotify,
  FaApple, FaSnapchat, FaPinterest, FaTelegram, FaDiscord,
  FaTwitch, FaReddit, FaMedium, FaBehance, FaDribbble, FaGlobe,
  FaSoundcloud, FaAmazon, FaEtsy, FaPatreon, FaMoneyBillWave, FaWallet,
} from 'react-icons/fa'

/* ---------- COULEURS (helpers) ---------- */
export const withAlpha = (hex, aa = '88') => (/^#[0-9a-f]{6}$/i.test(hex || '') ? hex + aa : hex)

export const isLightColor = (hex = '#000000') => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex)
  if (!m) return false
  const n = parseInt(m[1], 16)
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.62
}

/* ---------- THÈMES ---------- */
export const THEMES = [
  { id: 'classic', name: 'Classique', bg: '#0F1B3D', bg2: '#0F1B3D', accent: '#14B8A6', text: '#FFFFFF' },
  { id: 'midnight', name: 'Midnight', bg: '#000000', bg2: '#000000', accent: '#FFFFFF', text: '#FFFFFF' },
  { id: 'ocean', name: 'Océan', bg: '#0891b2', bg2: '#0F1B3D', accent: '#FFFFFF', text: '#FFFFFF' },
  { id: 'sunset', name: 'Sunset', bg: '#FF6B6B', bg2: '#FFD93D', accent: '#FFFFFF', text: '#FFFFFF' },
  { id: 'forest', name: 'Forêt', bg: '#065f46', bg2: '#022c22', accent: '#FCD34D', text: '#FFFFFF' },
  { id: 'rose', name: 'Rose', bg: '#F472B6', bg2: '#EC4899', accent: '#FFFFFF', text: '#FFFFFF' },
  { id: 'purple', name: 'Violet', bg: '#8B5CF6', bg2: '#4C1D95', accent: '#FFFFFF', text: '#FFFFFF' },
  { id: 'light', name: 'Clair', bg: '#F9FAFB', bg2: '#F9FAFB', accent: '#0F1B3D', text: '#0F1B3D' },
  { id: 'gold', name: 'Gold', bg: '#78350f', bg2: '#c9a24a', accent: '#FFFFFF', text: '#FFFFFF' },
  { id: 'teal', name: 'Teal', bg: '#006b5f', bg2: '#006b5f', accent: '#FFFFFF', text: '#FFFFFF' },
  { id: 'elegant', name: 'Élégant', bg: '#1A1A1A', bg2: '#1A1A1A', accent: '#c9a24a', text: '#FFFFFF' },
  { id: 'candy', name: 'Candy', bg: '#F9A8D4', bg2: '#C4B5FD', accent: '#FFFFFF', text: '#FFFFFF' },
]

export const getTheme = (settings = {}) => {
  if (settings.customColors) {
    return {
      bg: settings.customBg || '#0F1B3D',
      bg2: settings.customBg2 || settings.customBg || '#0F1B3D',
      accent: settings.customAccent || '#14B8A6',
      text: settings.customText || '#FFFFFF',
    }
  }
  const t = THEMES.find((th) => th.id === settings.theme) || THEMES[0]
  return t
}

export const getBgStyle = (theme, opacity = 100) => {
  const grad = theme.bg === theme.bg2
    ? theme.bg
    : `linear-gradient(160deg, ${theme.bg}, ${theme.bg2})`
  if (opacity >= 100) return { background: grad }
  const wash = theme.text === '#0F1B3D' ? 'rgba(255,255,255,' : 'rgba(0,0,0,'
  const a = (1 - opacity / 100).toFixed(2)
  return { backgroundImage: `linear-gradient(${wash}${a}), ${grad}` }
}

/** Fond complet : dégradé + éventuels halos lumineux (settings.bgGlow = ['#7c3aed','#ec4899']). */
export const makeBgStyle = (theme, settings = {}) => {
  const glow = Array.isArray(settings.bgGlow) ? settings.bgGlow : null
  if (!glow) return getBgStyle(theme, settings.opacity ?? 100)
  const grad = theme.bg === theme.bg2
    ? `linear-gradient(${theme.bg}, ${theme.bg})`
    : `linear-gradient(160deg, ${theme.bg}, ${theme.bg2})`
  return {
    backgroundColor: theme.bg,
    backgroundImage:
      `radial-gradient(circle at 10% 18%, ${withAlpha(glow[0], '77')}, transparent 42%),` +
      `radial-gradient(circle at 92% 24%, ${withAlpha(glow[1] || glow[0], '66')}, transparent 40%),` +
      grad,
  }
}

/* ---------- POLICES ---------- */
export const FONTS = [
  { id: 'inter', name: 'Inter', family: "'Inter', sans-serif", google: 'Inter:wght@400;500;600;700' },
  { id: 'jakarta', name: 'Plus Jakarta', family: "'Plus Jakarta Sans', sans-serif", google: 'Plus+Jakarta+Sans:wght@400;500;600;700' },
  { id: 'georgia', name: 'Georgia', family: 'Georgia, serif' },
  { id: 'playfair', name: 'Playfair', family: "'Playfair Display', serif", google: 'Playfair+Display:wght@400;500;600;700' },
  { id: 'mono', name: 'Monospace', family: "'Courier New', monospace" },
  { id: 'raleway', name: 'Raleway', family: "'Raleway', sans-serif", google: 'Raleway:wght@400;500;600;700' },
  { id: 'poppins', name: 'Poppins', family: "'Poppins', sans-serif", google: 'Poppins:wght@400;500;600;700' },
  { id: 'spacegrotesk', name: 'Space Grotesk', family: "'Space Grotesk', sans-serif", google: 'Space+Grotesk:wght@400;500;600;700' },
  // nouveaux (templates)
  { id: 'cormorant', name: 'Cormorant (serif chic)', family: "'Cormorant Garamond', Georgia, serif", google: 'Cormorant+Garamond:wght@300;400;500;600;700' },
  { id: 'montserrat', name: 'Montserrat', family: "'Montserrat', sans-serif", google: 'Montserrat:wght@300;400;500;600;700' },
  { id: 'cinzel', name: 'Cinzel (capitales)', family: "'Cinzel', Georgia, serif", google: 'Cinzel:wght@400;600' },
  { id: 'greatvibes', name: 'Great Vibes (script)', family: "'Great Vibes', cursive", google: 'Great+Vibes' },
  { id: 'allura', name: 'Allura (script)', family: "'Allura', cursive", google: 'Allura' },
]
export const getFont = (id) => FONTS.find((f) => f.id === id) || FONTS[0]

export const googleFontsHref = (ids = []) => {
  const fams = [...new Set(ids.filter(Boolean))]
    .map((id) => FONTS.find((f) => f.id === id)?.google)
    .filter(Boolean)
  return fams.length
    ? `https://fonts.googleapis.com/css2?${fams.map((f) => `family=${f}`).join('&')}&display=swap`
    : ''
}

/** Charge les polices Google utilisées (appel idempotent, à utiliser dans un useEffect). */
export const loadGoogleFonts = (ids = []) => {
  if (typeof document === 'undefined') return
  const href = googleFontsHref(ids)
  if (!href) return
  let el = document.getElementById('tapio-gfonts')
  if (!el) {
    el = document.createElement('link')
    el.id = 'tapio-gfonts'
    el.rel = 'stylesheet'
    document.head.appendChild(el)
  }
  if (el.href !== href) el.href = href
}

/* ---------- BOUTONS ---------- */
export const BUTTON_SHAPES = [
  { id: 'rounded', name: 'Arrondi', radius: '16px' },
  { id: 'pill', name: 'Pilule', radius: '999px' },
  { id: 'square', name: 'Carré', radius: '8px' },
  { id: 'sharp', name: 'Net', radius: '0px' },
  { id: 'soft', name: 'Doux', radius: '28px' },
]
export const getButtonShape = (id) => BUTTON_SHAPES.find((s) => s.id === id) || BUTTON_SHAPES[0]

export const BUTTON_STYLES = [
  { id: 'solid', name: 'Plein' },
  { id: 'outline', name: 'Contour' },
  { id: 'glass', name: 'Verre' },
  { id: 'shadow-hard', name: 'Ombre dure' },
]
export const getButtonStyle = (theme, id) => {
  switch (id) {
    case 'outline':
      return { border: `2px solid ${theme.accent}`, color: theme.accent, background: 'transparent' }
    case 'glass':
      return { background: 'rgba(255,255,255,0.15)', color: theme.text, backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.25)' }
    case 'shadow-hard':
      return { background: theme.accent, color: theme.text, border: `2px solid ${theme.text}`, boxShadow: `4px 4px 0 ${theme.text}` }
    case 'solid':
    default:
      return { background: theme.accent, color: theme.text }
  }
}

export const BUTTON_SIZES = {
  sm: 'py-2 px-3 text-xs',
  md: 'py-3 px-4 text-sm',
  lg: 'py-4 px-5 text-base',
}

export const HOVER_EFFECTS = [
  { id: 'none', name: 'Aucun', cls: '' },
  { id: 'scale', name: 'Zoom', cls: 'hover:scale-[1.03] active:scale-[0.98]' },
  { id: 'lift', name: 'Élévation', cls: 'hover:-translate-y-0.5 hover:shadow-lg' },
]
export const getHoverClass = (id) => (HOVER_EFFECTS.find((h) => h.id === id) || HOVER_EFFECTS[0]).cls

/* ---------- AVATAR / TEXTE / CARTES ---------- */
export const AVATAR_SHAPES = { circle: 'rounded-full', rounded: 'rounded-2xl', square: 'rounded-none', arch: 'rounded-t-full' }
export const AVATAR_SIZES = { sm: 'w-16 h-16', md: 'w-20 h-20', lg: 'w-28 h-28' }

export const TEXT_ALIGNS = [
  { id: 'left', name: 'Gauche', cls: 'text-left' },
  { id: 'center', name: 'Centre', cls: 'text-center' },
  { id: 'right', name: 'Droite', cls: 'text-right' },
]

export const CARD_SHADOWS = {
  none: '',
  sm: '0 1px 3px rgba(0,0,0,.15)',
  md: '0 6px 16px rgba(0,0,0,.18)',
  lg: '0 14px 34px rgba(0,0,0,.25)',
}
export const CARD_RADII = { none: '0px', sm: '8px', md: '16px', lg: '24px', full: '999px' }

/* ---------- TYPES DE BLOCS (parité BioSites) ---------- */
export const BLOCK_TYPES = [
  { id: 'header', label: 'Titre', icon: 'Type', color: 'bg-blue-50 text-blue-600' },
  { id: 'link', label: 'Lien', icon: 'Link', color: 'bg-teal-50 text-teal-600' },
  { id: 'image', label: 'Image', icon: 'Image', color: 'bg-pink-50 text-pink-600' },
  { id: 'video', label: 'Vidéo', icon: 'Video', color: 'bg-red-50 text-red-600' },
  { id: 'music', label: 'Musique', icon: 'Music', color: 'bg-green-50 text-green-600' },
  { id: 'social', label: 'Réseaux', icon: 'Globe', color: 'bg-purple-50 text-purple-600' },
  { id: 'map', label: 'Localisation', icon: 'MapPin', color: 'bg-orange-50 text-orange-600' },
  { id: 'contact', label: 'Formulaire', icon: 'Send', color: 'bg-indigo-50 text-indigo-600' },
  { id: 'email_capture', label: 'Capture email', icon: 'Mail', color: 'bg-amber-50 text-amber-600' },
  { id: 'shop', label: 'Produit', icon: 'ShoppingBag', color: 'bg-emerald-50 text-emerald-600' },
  { id: 'booking', label: 'Réservation', icon: 'Calendar', color: 'bg-cyan-50 text-cyan-600' },
  { id: 'faq', label: 'FAQ', icon: 'HelpCircle', color: 'bg-yellow-50 text-yellow-600' },
  { id: 'countdown', label: 'Compte à rebours', icon: 'Timer', color: 'bg-rose-50 text-rose-600' },
  { id: 'review', label: 'Avis', icon: 'Star', color: 'bg-fuchsia-50 text-fuchsia-600' },
  { id: 'embed', label: 'Intégration', icon: 'Code', color: 'bg-slate-100 text-slate-600' },
  { id: 'pdf', label: 'Document', icon: 'FileText', color: 'bg-sky-50 text-sky-600' },
  { id: 'text_divider', label: 'Séparateur texte', icon: 'Layout', color: 'bg-slate-100 text-slate-500' },
  { id: 'separator', label: 'Espace', icon: 'Minus', color: 'bg-slate-100 text-slate-600' },
]

export const LUCIDE_ICON_MAP = {
  Type, Link: LinkIcon, Image: ImageIcon, Video, Music, Globe, MapPin, Send,
  Minus, ShoppingBag, Mail, Calendar, HelpCircle, Code, FileText, Timer, Star, Layout,
  Heart, MessageCircle, Users, Tag, Sparkles, Scissors, Gift, Phone,
}

/* ---------- ICÔNES / PLATEFORMES ---------- */
export const ICON_CATEGORIES = {
  'Réseaux sociaux': [
    { id: 'instagram', label: 'Instagram', icon: FaInstagram, color: '#e1306c' },
    { id: 'facebook', label: 'Facebook', icon: FaFacebook, color: '#1877f2' },
    { id: 'twitter', label: 'X', icon: FaTwitter, color: '#1da1f2' },
    { id: 'linkedin', label: 'LinkedIn', icon: FaLinkedin, color: '#0a66c2' },
    { id: 'tiktok', label: 'TikTok', icon: FaTiktok, color: '#000000' },
    { id: 'youtube', label: 'YouTube', icon: FaYoutube, color: '#ff0000' },
    { id: 'snapchat', label: 'Snapchat', icon: FaSnapchat, color: '#fffc00' },
    { id: 'pinterest', label: 'Pinterest', icon: FaPinterest, color: '#e60023' },
    { id: 'twitch', label: 'Twitch', icon: FaTwitch, color: '#9146ff' },
    { id: 'discord', label: 'Discord', icon: FaDiscord, color: '#5865f2' },
    { id: 'telegram', label: 'Telegram', icon: FaTelegram, color: '#0088cc' },
    { id: 'reddit', label: 'Reddit', icon: FaReddit, color: '#ff4500' },
    { id: 'medium', label: 'Medium', icon: FaMedium, color: '#000000' },
    { id: 'behance', label: 'Behance', icon: FaBehance, color: '#1769ff' },
    { id: 'dribbble', label: 'Dribbble', icon: FaDribbble, color: '#ea4c89' },
  ],
  'Communication': [
    { id: 'whatsapp', label: 'WhatsApp', icon: FaWhatsapp, color: '#25d366' },
    { id: 'email', label: 'Email', icon: FaGlobe, color: '#0F1B3D' },
    { id: 'google', label: 'Google Reviews', icon: FaGoogle, color: '#ea4335' },
  ],
  'Musique / créateur': [
    { id: 'spotify', label: 'Spotify', icon: FaSpotify, color: '#1db954' },
    { id: 'apple_music', label: 'Apple Music', icon: FaApple, color: '#fa243c' },
    { id: 'soundcloud', label: 'SoundCloud', icon: FaSoundcloud, color: '#ff5500' },
    { id: 'patreon', label: 'Patreon', icon: FaPatreon, color: '#f96854' },
  ],
  'Commerce': [
    { id: 'amazon', label: 'Amazon', icon: FaAmazon, color: '#ff9900' },
    { id: 'etsy', label: 'Etsy', icon: FaEtsy, color: '#f56400' },
    { id: 'cashapp', label: 'Cash App', icon: FaMoneyBillWave, color: '#00d632' },
    { id: 'venmo', label: 'Venmo', icon: FaWallet, color: '#3d95ce' },
  ],
  'Autres': [
    { id: 'website', label: 'Site web', icon: FaGlobe, color: '#64748b' },
    { id: 'github', label: 'GitHub', icon: FaGithub, color: '#181717' },
  ],
}
export const ALL_PLATFORMS = Object.values(ICON_CATEGORIES).flat()
export const PLATFORMS = ALL_PLATFORMS.reduce((acc, p) => { acc[p.id] = p; return acc }, {})
export const getPlatform = (id) => PLATFORMS[id] || PLATFORMS.website

/** Icône d'un lien : plateforme (Instagram…) OU icône lucide générique (Calendar, Heart…). */
export const getLinkIcon = (id) => {
  if (PLATFORMS[id]) return { Icon: PLATFORMS[id].icon, color: PLATFORMS[id].color }
  if (LUCIDE_ICON_MAP[id]) return { Icon: LUCIDE_ICON_MAP[id], color: '#64748b' }
  return { Icon: PLATFORMS.website.icon, color: PLATFORMS.website.color }
}
export const GENERIC_LINK_ICONS = ['Calendar', 'Scissors', 'Heart', 'MessageCircle', 'ShoppingBag', 'Users', 'Tag', 'Gift', 'Phone', 'Mail', 'Sparkles', 'Star']

/* ============================================================
   MISE EN PAGE : listes d'options
   ============================================================ */
export const HEADER_LAYOUTS = [
  { id: 1, name: 'Cover courbée' },
  { id: 2, name: 'Grand nom sur cover' },
  { id: 3, name: 'Photos inclinées' },
  { id: 4, name: 'Cover + texte à gauche' },
  { id: 5, name: 'Photo plein écran (hero)' },
  { id: 6, name: 'Photo en arche' },
  { id: 7, name: 'Cercle + texte courbé' },
  { id: 8, name: 'Cadre + photo chevauchante' },
  { id: 9, name: 'Bandeau couleur + courbe' },
  { id: 10, name: 'Simple centré' },
]
export const LINK_LAYOUTS = [
  { id: 'button', name: 'Bouton' },
  { id: 'ornate', name: 'Bouton orné (étincelles)' },
  { id: 'card', name: 'Carte + icône + sous-titre' },
  { id: 'card-round', name: 'Grande carte ronde' },
]
export const LINK_ICON_STYLES = [
  { id: 'brand', name: 'Couleur de la marque' },
  { id: 'soft', name: 'Tuile claire' },
  { id: 'ring', name: 'Rond cerclé' },
  { id: 'none', name: 'Aucune' },
]
export const SUBTITLE_MODES = [
  { id: 'none', name: 'Aucun' },
  { id: 'text', name: 'Texte du lien' },
  { id: 'url', name: "Adresse (ou texte)" },
]
export const SOCIAL_LAYOUTS = [{ id: 'buttons', name: 'Boutons larges' }, { id: 'icons', name: 'Rangée d’icônes' }]
export const SOCIAL_STYLES = [
  { id: 'brand', name: 'Couleur de marque' },
  { id: 'round-solid', name: 'Rond plein' },
  { id: 'soft', name: 'Rond doux' },
  { id: 'square', name: 'Carré contour' },
  { id: 'plain', name: 'Icône seule' },
]
export const SOCIAL_POSITIONS = [{ id: 'inline', name: 'Où je les place' }, { id: 'top', name: 'Sous l’en-tête' }]
export const ORNAMENTS = [{ id: 'none', name: 'Aucun' }, { id: 'sparkle', name: 'Étincelle ✦' }, { id: 'diamond', name: 'Losange ◆' }]
export const PAGE_FRAMES = [{ id: 'none', name: 'Aucun' }, { id: 'thin', name: 'Cadre fin' }, { id: 'arch', name: 'Cadre en arche' }]
export const HERO_ALIGNS = [{ id: 'left', name: 'Gauche' }, { id: 'center', name: 'Centre' }, { id: 'right', name: 'Droite' }]
export const HEADLINE_POS = [{ id: 'above-links', name: 'Au-dessus des liens' }, { id: 'below-links', name: 'Sous les liens' }]

/* ============================================================
   TAILLES (largeur / hauteur) — 0 = automatique
   ============================================================ */
export const SIZE_LIMITS = {
  contentWidth: { label: 'Largeur de la page', min: 320, max: 720, step: 10, unit: 'px' },
  headerHeight: { label: "Hauteur de l'en-tête / cover", min: 80, max: 520, step: 10, unit: 'px', auto: true },
  avatarPx:     { label: 'Taille de la photo', min: 56, max: 200, step: 2, unit: 'px', auto: true },
  linkWidth:    { label: 'Largeur des liens', min: 50, max: 100, step: 1, unit: '%' },
  linkHeight:   { label: 'Hauteur des liens', min: 32, max: 130, step: 2, unit: 'px', auto: true },
  linkGap:      { label: 'Espace entre les liens', min: 4, max: 32, step: 2, unit: 'px' },
}
export const LAYOUT_DEFAULT_AVATAR = { 1: 80, 2: 110, 3: 110, 4: 64, 5: 0, 6: 130, 7: 130, 8: 110, 9: 84, 10: 104 }
export const LAYOUT_DEFAULT_HEADER = { 1: 130, 2: 230, 3: 0, 4: 110, 5: 400, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0 }
export const SIZE_PRESETS = [
  { id: 'compact', name: 'Compact', patch: { contentWidth: 420, linkWidth: 90, linkHeight: 40, linkGap: 8 } },
  { id: 'normal', name: 'Normal', patch: { contentWidth: 576, linkWidth: 100, linkHeight: 0, linkGap: 12 } },
  { id: 'large', name: 'Grand', patch: { contentWidth: 680, linkWidth: 100, linkHeight: 72, linkGap: 16 } },
]

/* ---------- NOUVEAUX RÉGLAGES (valeurs neutres = rendu historique) ---------- */
export const NEW_DEFAULTS = {
  template: '',
  // textes
  tagline: '', taglineColor: '', taglineUppercase: false,
  headline: '', headlinePos: 'above-links', headlineFont: '', headlineColor: '',
  footerText: '',
  nameFont: '', nameSize: 0, nameWeight: 0, nameUppercase: false, nameUnderline: false,
  // en-tête
  headerBg: '', heroAlign: 'right', avatarRing: '', avatarRingWidth: 0,
  // tailles
  contentWidth: 576, headerHeight: 0, avatarPx: 0, linkWidth: 100, linkHeight: 0, linkGap: 12,
  // liens
  linkLayout: 'button', linkIconStyle: 'soft', linkShowSubtitle: 'none',
  linkBg: '', linkText: '', linkBorder: '', linkUppercase: false,
  highlightFirst: false, linkHighlightBg: '', linkHighlightText: '',
  ornament: 'none', pageFrame: 'none',
  // réseaux
  socialLayout: 'buttons', socialStyle: 'round-solid', socialPosition: 'inline',
  socialLines: false, socialBg: '', socialFg: '',
  // divers
  surface: '', bgGlow: null,
}

/* ============================================================
   TEMPLATES
   ============================================================ */
const T = (id, name, tag, s, cat) => ({
  id, name, tag, cat,
  settings: { customColors: true, designStyle: 'minimal', ...s },
})

export const TEMPLATES = [
  /* 1 — Beauty designer (hero + grandes cartes dégradées) */
  T('rose-gold', 'Rose Gold Beauty', 'Hero · grandes cartes', {
    customBg: '#0b0807', customBg2: '#1c120d', customAccent: '#d8b49b', customText: '#f6ece4',
    font: 'cormorant', nameFont: 'cormorant', nameUppercase: true, nameWeight: 400, nameSize: 30,
    headlineFont: 'greatvibes',
    headerLayout: 5, heroAlign: 'right', headerHeight: 400,
    tagline: 'Beauty designer', taglineUppercase: true, taglineColor: '#d8b49b',
    headline: 'Sua melhor versão, até nas unhas.', headlinePos: 'above-links', headlineColor: '#d8b49b',
    linkLayout: 'card-round', linkIconStyle: 'ring', linkShowSubtitle: 'text',
    linkBg: 'linear-gradient(100deg,#f7efe9,#b99c85)', linkText: '#4a3426',
    linkWidth: 86, linkHeight: 92, linkGap: 14, buttonShape: 'soft', buttonHover: 'lift', shadow: 'md',
    socialLayout: 'icons', socialStyle: 'square', socialPosition: 'top', socialLines: true,
    contentWidth: 480,
  }),

  /* 2a — Noir, cercle sur cadre fin, liens contour ornés */
  T('noir-frame', 'Noir · Cadre', 'Cercle + cadre fin', {
    customBg: '#222222', customBg2: '#222222', customAccent: '#ffffff', customText: '#f2efe9',
    font: 'montserrat', nameFont: 'cormorant', nameWeight: 300, nameSize: 30,
    headerLayout: 8, avatarPx: 108, pageFrame: 'thin',
    linkLayout: 'ornate', ornament: 'sparkle', linkBg: 'transparent', linkText: '#f2efe9', linkBorder: '#f2efe9',
    buttonShape: 'square', buttonSize: 'sm', linkWidth: 82, linkHeight: 38, linkGap: 10, linkUppercase: true, shadow: 'none',
    socialLayout: 'icons', socialStyle: 'round-solid', socialBg: '#f2efe9', socialFg: '#222222', socialPosition: 'inline',
    contentWidth: 420,
  }),

  /* 2b — Noir, photo en arche, pilules blanches */
  T('noir-arch', 'Noir · Arche', 'Arche + pilules blanches', {
    customBg: '#222222', customBg2: '#222222', customAccent: '#ffffff', customText: '#f2efe9',
    font: 'montserrat', nameFont: 'cormorant', nameWeight: 300, nameSize: 30,
    headerLayout: 6, avatarPx: 120, pageFrame: 'arch', avatarShape: 'arch',
    linkLayout: 'ornate', ornament: 'sparkle', linkBg: '#ffffff', linkText: '#222222', linkBorder: '',
    buttonShape: 'pill', buttonSize: 'sm', linkWidth: 78, linkHeight: 34, linkGap: 12, linkUppercase: true, shadow: 'none',
    socialLayout: 'icons', socialStyle: 'round-solid', socialBg: '#f2efe9', socialFg: '#222222', socialPosition: 'inline',
    contentWidth: 420,
  }),

  /* 2c — Noir, cercle + texte courbé */
  T('noir-circle', 'Noir · Texte courbé', 'Cercle + nom en arc', {
    customBg: '#222222', customBg2: '#222222', customAccent: '#ffffff', customText: '#f2efe9',
    font: 'montserrat', nameFont: 'cormorant', nameWeight: 300,
    headerLayout: 7, avatarPx: 124, avatarShape: 'circle',
    tagline: 'Profession', taglineUppercase: true,
    linkLayout: 'ornate', ornament: 'sparkle', linkBg: '#f3f1ed', linkText: '#222222',
    highlightFirst: true, linkHighlightBg: '#ffffff', linkHighlightText: '#222222',
    buttonShape: 'square', buttonSize: 'sm', linkWidth: 82, linkHeight: 36, linkGap: 10, linkUppercase: true, shadow: 'none',
    socialLayout: 'icons', socialStyle: 'round-solid', socialBg: '#f2efe9', socialFg: '#222222', socialPosition: 'inline',
    contentWidth: 420,
  }),

  /* 2d — Noir, arche + pilules contour */
  T('noir-arch-outline', 'Noir · Arche contour', 'Arche + pilules fines', {
    customBg: '#222222', customBg2: '#222222', customAccent: '#ffffff', customText: '#f2efe9',
    font: 'montserrat', nameFont: 'cormorant', nameWeight: 300, nameSize: 30,
    headerLayout: 6, avatarPx: 120, pageFrame: 'arch', avatarShape: 'arch',
    linkLayout: 'ornate', ornament: 'sparkle', linkBg: 'transparent', linkText: '#f2efe9', linkBorder: '#f2efe9',
    buttonShape: 'pill', buttonSize: 'sm', linkWidth: 78, linkHeight: 34, linkGap: 12, linkUppercase: true, shadow: 'none',
    socialLayout: 'icons', socialStyle: 'round-solid', socialBg: '#f2efe9', socialFg: '#222222', socialPosition: 'inline',
    contentWidth: 420,
  }),

  /* 3 — UGC creator (bandeau rose + corps charbon courbé) */
  T('ugc-blush', 'UGC Blush', 'Bandeau rose + courbe', {
    customBg: '#3a3433', customBg2: '#3a3433', customAccent: '#f3d9d7', customText: '#f8ecea',
    headerBg: '#f2dfdf',
    font: 'montserrat', nameFont: 'cormorant', nameSize: 30, nameWeight: 500, nameUnderline: true,
    headlineFont: 'greatvibes',
    headerLayout: 9, avatarPx: 84,
    tagline: 'UGC Creator', taglineUppercase: true, taglineColor: '#f2dfdf',
    headline: "let's be friends!", headlinePos: 'below-links', headlineColor: '#f2dfdf',
    linkLayout: 'button', linkBg: '#f3d9d7', linkText: '#3a3433', linkUppercase: true,
    buttonShape: 'pill', buttonSize: 'sm', linkWidth: 90, linkHeight: 34, linkGap: 10, shadow: 'none',
    socialLayout: 'icons', socialStyle: 'round-solid', socialBg: '#f8ecea', socialFg: '#3a3433', socialPosition: 'inline',
    contentWidth: 400,
  }),

  /* 4 — Beige minimaliste, boutons ornés losange */
  T('beige-ornate', 'Beige Minimal', 'Serif · liens ornés', {
    customBg: '#e9e1d9', customBg2: '#e9e1d9', customAccent: '#4a2418', customText: '#4a2418',
    font: 'cormorant', nameFont: 'cormorant', nameUppercase: true, nameWeight: 500, nameSize: 30,
    headerLayout: 10, avatarPx: 104, avatarRing: '#c07a3a', avatarRingWidth: 3,
    tagline: 'Make-up artist', taglineUppercase: true,
    linkLayout: 'ornate', ornament: 'diamond', linkBg: 'transparent', linkText: '#4a2418', linkBorder: '#4a2418',
    buttonShape: 'pill', buttonSize: 'sm', linkWidth: 88, linkHeight: 44, linkGap: 12, linkUppercase: true, shadow: 'none',
    socialLayout: 'icons', socialStyle: 'round-solid', socialBg: '#4a2418', socialFg: '#e9e1d9', socialPosition: 'inline',
    contentWidth: 420,
  }),

  /* 5a — Rouge, photo carrée encadrée */
  T('red-square', 'Rouge · Photo carrée', 'Cadre blanc + pilules bordeaux', {
    customBg: '#8a1010', customBg2: '#3d0505', customAccent: '#5a0d0d', customText: '#ffffff',
    font: 'montserrat', nameFont: 'montserrat', nameWeight: 500, nameSize: 22, nameUppercase: true,
    headerLayout: 10, avatarPx: 96, avatarShape: 'square', avatarRing: '#ffffff', avatarRingWidth: 3,
    tagline: 'Makeup artist | Entrepreneur',
    linkLayout: 'button', linkBg: '#5a0d0d', linkText: '#ffffff', linkUppercase: true,
    buttonShape: 'rounded', buttonSize: 'sm', linkWidth: 90, linkHeight: 38, linkGap: 10, shadow: 'sm',
    socialLayout: 'icons', socialStyle: 'round-solid', socialBg: '#f4dede', socialFg: '#5a0d0d', socialPosition: 'inline',
    contentWidth: 400,
  }),

  /* 5b — Rouge, photo plein cadre + liens givrés */
  T('red-cover', 'Rouge · Plein cadre', 'Hero + liens givrés', {
    customBg: '#8a1010', customBg2: '#3d0505', customAccent: '#ffffff', customText: '#ffffff',
    font: 'montserrat', nameFont: 'montserrat', nameWeight: 500, nameSize: 26, nameUppercase: true,
    headerLayout: 5, heroAlign: 'center', headerHeight: 320,
    tagline: 'Makeup artist | Entrepreneur',
    linkLayout: 'button', linkBg: 'rgba(255,255,255,0.55)', linkText: '#7a1010', linkUppercase: true,
    buttonShape: 'pill', buttonSize: 'sm', linkWidth: 88, linkHeight: 38, linkGap: 10, shadow: 'none',
    socialLayout: 'icons', socialStyle: 'round-solid', socialBg: '#ffffff', socialFg: '#7a1010', socialPosition: 'inline',
    contentWidth: 400,
  }),

  /* 5c — Rouge, avatar rond + réseaux en haut */
  T('red-circle', 'Rouge · Cercle', 'Avatar rond + réseaux', {
    customBg: '#8a1010', customBg2: '#3d0505', customAccent: '#5a0d0d', customText: '#ffffff',
    font: 'montserrat', nameFont: 'montserrat', nameWeight: 500, nameSize: 22, nameUppercase: true,
    headerLayout: 10, avatarPx: 84, avatarShape: 'circle', avatarRing: '#ffffff', avatarRingWidth: 2,
    tagline: 'Makeup artist | Fashion model',
    linkLayout: 'button', linkBg: '#5a0d0d', linkText: '#ffffff', linkUppercase: true,
    buttonShape: 'pill', buttonSize: 'sm', linkWidth: 90, linkHeight: 36, linkGap: 10, shadow: 'sm',
    socialLayout: 'icons', socialStyle: 'round-solid', socialBg: '#f4dede', socialFg: '#5a0d0d', socialPosition: 'top',
    contentWidth: 400,
  }),

  /* 6 — Dev néon (cartes verre + icônes couleur marque) */
  T('neon-dev', 'Néon Dev', 'Cartes verre · icônes couleur', {
    customBg: '#0b0716', customBg2: '#160a30', customAccent: '#c026d3', customText: '#ffffff',
    bgGlow: ['#7c3aed', '#ec4899'],
    font: 'inter', nameSize: 26, nameWeight: 700,
    headerLayout: 10, avatarPx: 120, avatarShape: 'circle',
    avatarRing: 'linear-gradient(135deg,#ec4899,#6366f1)', avatarRingWidth: 4, verifiedBadge: true,
    tagline: 'Full Stack Developer', taglineColor: '#a78bfa',
    linkLayout: 'card', linkIconStyle: 'brand', linkShowSubtitle: 'url',
    linkBg: 'rgba(255,255,255,0.06)', linkText: '#ffffff', linkBorder: 'rgba(255,255,255,0.10)',
    highlightFirst: true, linkHighlightBg: 'linear-gradient(90deg,#ec4899,#7c3aed 60%,#4c1d95)', linkHighlightText: '#ffffff',
    buttonShape: 'rounded', linkWidth: 100, linkHeight: 64, linkGap: 10, shadow: 'none',
    socialLayout: 'icons', socialStyle: 'soft', socialPosition: 'top',
    contentWidth: 440,
  }),

  /* 7 — Blush Style (cartes beiges, newsletter) */
  T('blush-style', 'Blush Style', 'Cartes douces · serif', {
    customBg: '#f6efec', customBg2: '#f6efec', customAccent: '#b99486', customText: '#2b2320',
    font: 'jakarta', nameFont: 'playfair', nameSize: 32, nameWeight: 500,
    headerLayout: 10, avatarPx: 112, avatarShape: 'circle', avatarRing: '#c9a79b', avatarRingWidth: 3, verifiedBadge: true,
    tagline: 'Fashion • Style • Lifestyle', taglineUppercase: true, taglineColor: '#b99486',
    linkLayout: 'card', linkIconStyle: 'soft', linkShowSubtitle: 'text',
    linkBg: '#eadad3', linkText: '#2b2320', highlightFirst: true, linkHighlightBg: '#d8bcb2', linkHighlightText: '#2b2320',
    buttonShape: 'square', linkWidth: 100, linkHeight: 60, linkGap: 8, shadow: 'none',
    socialLayout: 'icons', socialStyle: 'soft', socialPosition: 'top',
    contentWidth: 440,
  }),

  /* ===================== PRO · Business ===================== */
  T('pro-navy', 'Pro Navy', 'Cartes + icônes de marque', {
    customBg: '#0b1530', customBg2: '#0F1B3D', customAccent: '#14B8A6', customText: '#ffffff',
    font: 'inter', nameSize: 24, nameWeight: 700, verifiedBadge: true,
    headerLayout: 10, avatarPx: 96, avatarShape: 'circle', avatarRing: '#14B8A6', avatarRingWidth: 3,
    tagline: 'Consultant · Entrepreneur', taglineColor: '#5eead4',
    linkLayout: 'card', linkIconStyle: 'brand', linkShowSubtitle: 'url',
    linkBg: 'rgba(255,255,255,0.07)', linkText: '#ffffff', linkBorder: 'rgba(255,255,255,0.12)',
    highlightFirst: true, linkHighlightBg: '#14B8A6', linkHighlightText: '#04201c',
    buttonShape: 'rounded', linkWidth: 100, linkHeight: 62, linkGap: 10, shadow: 'none',
    socialLayout: 'icons', socialStyle: 'brand', socialPosition: 'top', contentWidth: 440,
  }, 'Business'),

  T('pro-white', 'Pro Clair', 'Blanc épuré · bleu', {
    customBg: '#f8fafc', customBg2: '#eef2f7', customAccent: '#2563eb', customText: '#0f172a',
    font: 'inter', nameSize: 24, nameWeight: 700, verifiedBadge: true,
    headerLayout: 10, avatarPx: 92, avatarShape: 'circle', avatarRing: '#ffffff', avatarRingWidth: 4,
    tagline: 'Agence · Studio', taglineColor: '#2563eb',
    linkLayout: 'card', linkIconStyle: 'brand', linkShowSubtitle: 'text',
    linkBg: '#ffffff', linkText: '#0f172a', linkBorder: '#e2e8f0',
    highlightFirst: true, linkHighlightBg: '#2563eb', linkHighlightText: '#ffffff',
    buttonShape: 'rounded', linkWidth: 100, linkHeight: 60, linkGap: 10, shadow: 'sm',
    socialLayout: 'icons', socialStyle: 'brand', socialPosition: 'top', contentWidth: 440,
  }, 'Business'),

  T('pro-slate', 'Ardoise', 'Sobre · boutons pleins', {
    customBg: '#1e293b', customBg2: '#0f172a', customAccent: '#f8fafc', customText: '#f8fafc',
    font: 'jakarta', nameSize: 26, nameWeight: 600,
    headerLayout: 1, headerHeight: 120, avatarPx: 88, avatarShape: 'rounded', avatarRing: '#f8fafc', avatarRingWidth: 3,
    tagline: 'Avocat · Conseil', taglineUppercase: true,
    linkLayout: 'button', linkBg: '#f8fafc', linkText: '#0f172a',
    buttonShape: 'square', buttonSize: 'md', linkWidth: 100, linkHeight: 48, linkGap: 10, shadow: 'none',
    socialLayout: 'icons', socialStyle: 'round-solid', socialPosition: 'top', contentWidth: 440,
  }, 'Business'),

  /* ===================== Créateur ===================== */
  T('creator-gradient', 'Créateur Dégradé', 'Violet · pilules vitrées', {
    customBg: '#4c1d95', customBg2: '#be185d', customAccent: '#ffffff', customText: '#ffffff',
    font: 'poppins', nameSize: 26, nameWeight: 700, verifiedBadge: true,
    headerLayout: 10, avatarPx: 108, avatarShape: 'circle',
    avatarRing: 'linear-gradient(135deg,#fde68a,#f472b6)', avatarRingWidth: 4,
    tagline: 'Content creator', taglineColor: '#fde68a',
    linkLayout: 'button', linkBg: 'rgba(255,255,255,0.16)', linkText: '#ffffff', linkBorder: 'rgba(255,255,255,0.35)',
    highlightFirst: true, linkHighlightBg: '#ffffff', linkHighlightText: '#4c1d95',
    buttonShape: 'pill', buttonSize: 'md', linkWidth: 100, linkHeight: 50, linkGap: 12, shadow: 'md',
    socialLayout: 'icons', socialStyle: 'brand', socialPosition: 'top', contentWidth: 420,
  }, 'Créateur'),

  T('creator-hero', 'Créateur Hero', 'Grande photo + cartes', {
    customBg: '#111111', customBg2: '#111111', customAccent: '#ffffff', customText: '#ffffff',
    font: 'montserrat', nameSize: 30, nameWeight: 700, nameUppercase: true,
    headerLayout: 5, heroAlign: 'center', headerHeight: 380,
    tagline: 'Vidéaste · Voyageur', taglineUppercase: true,
    linkLayout: 'card', linkIconStyle: 'brand', linkShowSubtitle: 'none',
    linkBg: 'rgba(255,255,255,0.10)', linkText: '#ffffff', linkBorder: 'rgba(255,255,255,0.18)',
    buttonShape: 'rounded', linkWidth: 100, linkHeight: 58, linkGap: 10, shadow: 'none',
    socialLayout: 'icons', socialStyle: 'brand', socialPosition: 'top', contentWidth: 420,
  }, 'Créateur'),

  T('brutal', 'Brutaliste', 'Ombre dure · couleurs vives', {
    customBg: '#fde047', customBg2: '#fde047', customAccent: '#ffffff', customText: '#111111',
    font: 'spacegrotesk', nameSize: 28, nameWeight: 700, nameUppercase: true,
    headerLayout: 10, avatarPx: 96, avatarShape: 'square', avatarRing: '#111111', avatarRingWidth: 4,
    tagline: 'Designer indépendant', taglineColor: '#111111', taglineUppercase: true,
    linkLayout: 'button', linkBg: '#ffffff', linkText: '#111111', linkBorder: '#111111',
    buttonStyle: 'shadow-hard', buttonShape: 'sharp', buttonSize: 'md', linkWidth: 100, linkHeight: 50, linkGap: 14, linkUppercase: true, shadow: 'none',
    socialLayout: 'icons', socialStyle: 'square', socialPosition: 'top', contentWidth: 420,
  }, 'Créateur'),

  /* ===================== Musique ===================== */
  T('music-dark', 'Musique Noir', 'Noir · vert Spotify', {
    customBg: '#050505', customBg2: '#111111', customAccent: '#1db954', customText: '#ffffff',
    font: 'montserrat', nameSize: 30, nameWeight: 800, nameUppercase: true,
    headerLayout: 2, headerHeight: 240, avatarPx: 104, avatarShape: 'circle', avatarRing: '#1db954', avatarRingWidth: 3,
    tagline: 'Artiste · Producteur', taglineColor: '#1db954', taglineUppercase: true,
    linkLayout: 'card', linkIconStyle: 'brand', linkShowSubtitle: 'text',
    linkBg: '#181818', linkText: '#ffffff', linkBorder: '#2a2a2a',
    highlightFirst: true, linkHighlightBg: '#1db954', linkHighlightText: '#04130a',
    buttonShape: 'rounded', linkWidth: 100, linkHeight: 62, linkGap: 10, shadow: 'none',
    socialLayout: 'icons', socialStyle: 'brand', socialPosition: 'top', contentWidth: 440,
  }, 'Musique'),

  /* ===================== Lifestyle ===================== */
  T('pastel-soft', 'Pastel Doux', 'Rose · lilas · arrondi', {
    customBg: '#fbcfe8', customBg2: '#ddd6fe', customAccent: '#ffffff', customText: '#4a2545',
    font: 'poppins', nameSize: 26, nameWeight: 600, verifiedBadge: true,
    headerLayout: 6, avatarPx: 124, avatarShape: 'arch', avatarRing: '#ffffff', avatarRingWidth: 4,
    tagline: 'Lifestyle · Mode', taglineColor: '#a21caf',
    linkLayout: 'button', linkBg: 'rgba(255,255,255,0.75)', linkText: '#4a2545',
    highlightFirst: true, linkHighlightBg: '#a21caf', linkHighlightText: '#ffffff',
    buttonShape: 'soft', buttonSize: 'md', linkWidth: 100, linkHeight: 50, linkGap: 12, shadow: 'sm',
    socialLayout: 'icons', socialStyle: 'brand', socialPosition: 'top', contentWidth: 420,
  }, 'Lifestyle'),

  T('forest-calm', 'Nature', 'Vert profond · or', {
    customBg: '#064e3b', customBg2: '#022c22', customAccent: '#fcd34d', customText: '#ecfdf5',
    font: 'cormorant', nameFont: 'cormorant', nameSize: 32, nameWeight: 600, nameUppercase: true,
    headerLayout: 7, avatarPx: 120, avatarShape: 'circle',
    tagline: 'Yoga · Bien-être', taglineUppercase: true, taglineColor: '#fcd34d',
    linkLayout: 'ornate', ornament: 'diamond', linkBg: 'transparent', linkText: '#ecfdf5', linkBorder: '#fcd34d',
    buttonShape: 'pill', buttonSize: 'sm', linkWidth: 84, linkHeight: 42, linkGap: 12, linkUppercase: true, shadow: 'none',
    socialLayout: 'icons', socialStyle: 'plain', socialPosition: 'top', socialLines: true, contentWidth: 420,
  }, 'Lifestyle'),

  /* ===================== Commerce ===================== */
  T('shop-warm', 'Boutique', 'Crème · terracotta', {
    customBg: '#fff7ed', customBg2: '#ffedd5', customAccent: '#c2410c', customText: '#431407',
    font: 'jakarta', nameFont: 'playfair', nameSize: 30, nameWeight: 600,
    headerLayout: 9, headerBg: '#c2410c', avatarPx: 88, avatarRing: '#ffffff', avatarRingWidth: 3,
    tagline: 'Artisanat · Fait main', taglineColor: '#c2410c', taglineUppercase: true,
    linkLayout: 'card', linkIconStyle: 'soft', linkShowSubtitle: 'text',
    linkBg: '#ffffff', linkText: '#431407', linkBorder: '#fed7aa',
    highlightFirst: true, linkHighlightBg: '#c2410c', linkHighlightText: '#ffffff',
    buttonShape: 'rounded', linkWidth: 100, linkHeight: 62, linkGap: 10, shadow: 'sm',
    socialLayout: 'icons', socialStyle: 'brand', socialPosition: 'top', contentWidth: 440,
  }, 'Commerce'),

  T('resto-dark', 'Restaurant', 'Noir chaud · carte ronde', {
    customBg: '#1c1410', customBg2: '#0d0907', customAccent: '#e8b86d', customText: '#f7efe4',
    font: 'cormorant', nameFont: 'cinzel', nameSize: 26, nameWeight: 600, nameUppercase: true,
    headerLayout: 8, avatarPx: 104, pageFrame: 'thin', avatarRing: '#e8b86d', avatarRingWidth: 2,
    tagline: 'Cuisine · Réservation', taglineUppercase: true, taglineColor: '#e8b86d',
    linkLayout: 'card-round', linkIconStyle: 'ring', linkShowSubtitle: 'text',
    linkBg: 'linear-gradient(100deg,#2a1d14,#3b2a1c)', linkText: '#f7efe4', linkBorder: '#5b4429',
    buttonShape: 'soft', linkWidth: 88, linkHeight: 84, linkGap: 12, shadow: 'md',
    socialLayout: 'icons', socialStyle: 'square', socialPosition: 'top', contentWidth: 440,
  }, 'Commerce'),
]

const KEEP_USER_TEXT = ['tagline', 'headline', 'footerText']

/**
 * Patch à fusionner dans les settings quand on choisit un template.
 * - remet à zéro tous les réglages "design" (pas de restes d'un ancien template)
 * - conserve ce que l'utilisateur a déjà écrit (tagline, headline, footerText) et ses images
 */
export const getTemplatePatch = (current = {}, tpl) => {
  const keep = {}
  KEEP_USER_TEXT.forEach((k) => { if (current[k]) keep[k] = current[k] })
  return { ...NEW_DEFAULTS, designStyle: 'minimal', ...tpl.settings, ...keep, template: tpl.id }
}

/* ---------- SETTINGS PAR DÉFAUT ---------- */
export const DEFAULT_SETTINGS = {
  theme: 'classic',
  customColors: false,
  customBg: '#0F1B3D',
  customBg2: '#0F1B3D',
  customAccent: '#14B8A6',
  customText: '#FFFFFF',
  font: 'inter',
  buttonShape: 'rounded',
  buttonStyle: 'solid',
  buttonSize: 'md',
  buttonHover: 'scale',
  avatarShape: 'circle',
  avatarSize: 'md',
  textAlign: 'center',
  shadow: 'md',
  borderRadius: 'md',
  opacity: 100,
  // Paramètres "page" BioSites-like
  verifiedBadge: false,
  passwordProtected: false,
  pagePassword: '',
  seoTitle: '',
  seoDescription: '',
  seoImage: '',
  analyticsEnabled: true,
  sensitiveContentWarning: false,
  // Templates + tailles
  ...NEW_DEFAULTS,
}